import React, { Dispatch } from "react";
import { RouteComponentProps } from "react-router-dom";
import { connect } from "react-redux";
import { Row, Container, Col } from "reactstrap";
import { showSnack } from "react-redux-snackbar";
import queryString from "query-string";
import {
  IAppState,
  LoadingDisplay,
  ApiRequest,
  appPaths,
  LoadingState,
  ErrorDisplay
} from "../../../Common";
import { Application } from "../../../App/Models/Application";
import FeatureRequestForm from "./FeatureRequestForm";
import BugReportForm from "./BugReportForm";
import ResourceRequestForm from "./ResourceRequestForm";
import { endpoints } from "../../Constants/endpoints";
import ResponseHandler from "../../../App/ResponseHandler";
import { ResponseResult } from "../../../Common/Models/ResponseResult";
import { QueryReportType } from "../../Constants/QueryReportType";
import { FeedbackParentDropdown } from "../Common/FeedbackDropdowns/FeedbackParentDropdown";
import { FeedbackParent, FeedbackCategory } from "../../Constants/feedbackEnums";
import FeedbackAppDropdown from "../Common/FeedbackDropdowns/FeedbackAppDropdown";
import { FeedbackCategoryDropdown } from "../Common/FeedbackDropdowns/FeedbackCategoryDropdown";
import { Topic } from "../../../Topic/Models/Topic";
import FeedbackTopicDropdown from "../Common/FeedbackDropdowns/FeedbackTopicDropdown";
import styles from "../../Styles/SubmitFeedbackContainer.module.scss";

type State = {
  activeReportType: string;
  userHasSelected: boolean;
  code?: string;
  codeIsGenerated: boolean;
  hasError: boolean;
  errorCode: number | null;
  errorDescription: string;
  errorReason: string;
  didDetermineDefaultApp: boolean;

  selectedParent: FeedbackParent;
  selectedApp?: Application;
  selectedTopic?: Topic;
};

type StateProps = {
  appsLoadingState: LoadingState;
  topicsLoadingState: LoadingState;

  applications: Application[];
  topics: Topic[];

  activeApp?: Application;
  activeTopic?: Topic;
  parentType: FeedbackParent;
};

type DispatchProps = {
  showSnack: (uniqueId: any, args: any) => void;
};

const mapStateToProps = (state: IAppState): StateProps => ({
  appsLoadingState: state.app.appsLoadingState,
  topicsLoadingState: state.app.topicsLoadingState,

  applications: state.app.applications,
  topics: state.app.topics,

  activeApp: state.app.activeApp,
  activeTopic: state.app.activeTopic,
  parentType: state.app.parentType
});

const mapDispatchToProps = (dispatch: Dispatch<any>): DispatchProps => ({
  showSnack: (uniqueId: any, args: any) => dispatch(showSnack(uniqueId, args))
});

class SubmitFeedbackContainer extends React.Component<
  DispatchProps & RouteComponentProps & StateProps,
  State
> {
  constructor(props: DispatchProps & RouteComponentProps & StateProps) {
    super(props);

    this.state = {
      activeReportType: QueryReportType.Feature,
      userHasSelected: false,
      codeIsGenerated: false,
      hasError: false,
      errorCode: null,
      errorDescription: "",
      errorReason: "",
      didDetermineDefaultApp: false,

      selectedApp: this.props.activeApp,
      selectedTopic: this.props.activeTopic,
      selectedParent: this.props.parentType
    };

    this.onCategoryDropdownChange = this.onCategoryDropdownChange.bind(this);
  }

  componentDidUpdate = (
    prevProps: DispatchProps & RouteComponentProps & StateProps,
    prevState: State
  ) => {
    if (!this.props.appsLoadingState.isLoaded || !this.props.topicsLoadingState.isLoaded) {
      return;
    }

    if (this.props.location.search.length === 0) {
      this.setReportType(QueryReportType.Feature, true);
    }

    const previousReportType = prevState.activeReportType;
    const parsedQuery = queryString.parse(this.props.location.search);
    const currentReportType = parsedQuery.type as string;

    if (previousReportType !== currentReportType && currentReportType !== undefined) {
      this.setReportType(currentReportType, true);
    }

    if (!this.state.userHasSelected) {
      if (parsedQuery.app) {
        // case where default app is defined
        if (!this.state.didDetermineDefaultApp) {
          const defaultApp = this.getDefaultApp();

          if (defaultApp) {
            this.setState({
              selectedParent: FeedbackParent.Application,
              selectedApp: defaultApp,
              selectedTopic: undefined,
              didDetermineDefaultApp: true
            });
          } else {
            throw new Error(`Could not find app with ID ${parsedQuery.app}`);
          }
        }
      } else if (parsedQuery.topic) {
        if (!this.state.didDetermineDefaultApp) {
          // case where default topic is defined
          const defaultTopic = this.getDefaultTopic();

          if (defaultTopic) {
            this.setState({
              selectedParent: FeedbackParent.Topic,
              selectedApp: undefined,
              selectedTopic: defaultTopic,
              didDetermineDefaultApp: true
            });
          } else {
            throw new Error(`Could not find topic with code ${parsedQuery.topic}`);
          }
        }
      } else {
        switch (this.props.parentType) {
          case FeedbackParent.Application:
            if (
              ((!!this.state.selectedApp &&
                !!this.props.activeApp &&
                this.state.selectedApp.rolePrefix !== this.props.activeApp.rolePrefix) ||
                !this.state.selectedApp) &&
              !this.state.didDetermineDefaultApp
            ) {
              this.setState({
                selectedParent: FeedbackParent.Application,
                selectedApp: this.props.activeApp,
                selectedTopic: undefined
              });
            }

            break;
          case FeedbackParent.Topic:
            if (
              ((!!this.state.selectedTopic &&
                !!this.props.activeTopic &&
                this.state.selectedTopic.code !== this.props.activeTopic.code) ||
                !this.state.selectedTopic) &&
              !this.state.didDetermineDefaultApp
            ) {
              this.setState({
                selectedParent: FeedbackParent.Topic,
                selectedTopic: this.props.activeTopic,
                selectedApp: undefined
              });
            }

            break;
          default:
            throw new Error("Invalid parent type");
        }
      }
    }
  };

  // gets the app that should have been default
  // this is the "this.props.activeApp" unless the query string "app" is specified
  getDefaultApp() {
    const appId = queryString.parse(this.props.location.search).app as string;

    const app = this.props.applications.find(a => a.rolePrefix === appId) as Application;
    if (app) {
      return app;
    }
    return undefined;
  }

  // gets the topic that should be default
  // this is the "this.props.activeTopic" unless the query string "topic" is specified
  getDefaultTopic() {
    const topicCode = queryString.parse(this.props.location.search).topic as string;

    const topic = this.props.topics.find(t => t.code === topicCode) as Topic;
    if (topic) {
      return topic;
    }

    return undefined;
  }

  componentDidMount = () => {
    if (this.props.location.search.length !== 0) {
      const type = queryString.parse(this.props.location.search).type as string;

      if (this.state.activeReportType !== type) {
        this.setReportType(type, true);
      }
    } else {
      this.setReportType(QueryReportType.Feature, true);
    }

    this.setState({
      selectedApp: this.props.activeApp
    });

    this.generateFeedbackCode();
  };

  generateFeedbackCode = () => {
    ApiRequest.fromPath(endpoints.feedback.generateCode())
      .withAuthHeader()
      .get()
      .then((response: Response) => {
        ResponseHandler.handle(
          response,
          (result: ResponseResult<string>) => {
            this.setState({
              code: result.payload,
              codeIsGenerated: true
            });
          },
          (result: ResponseResult) => {
            this.setState({
              errorCode: response.status,
              errorReason: "Failed to generate feedback code",
              errorDescription: result.error.message
            });

            this.showErrorSnackbar(
              response.status,
              result.error.message,
              "Failed to generate feedback code"
            );
          },
          () => {
            this.setState({
              errorCode: response.status,
              errorReason: "Failed to generate feedback code",
              errorDescription: "An unknown error has occured"
            });
            this.showErrorSnackbar(
              response.status,
              "An uknown error has occured",
              "Failed to generate feedback code"
            );
          }
        );
      });
  };

  onAppDropdownSelected = (application: Application) => {
    this.setState({
      selectedApp: application,
      userHasSelected: true
    });
  };

  setReportType = (type: string, replace: boolean = false) => {
    this.setState(
      {
        activeReportType: type
      },
      () => this.redirectToReport(type, replace)
    );
  };

  redirectToReport = (type: string, replace: boolean = false) => {
    // get the existing query, adjust the type, and then restringify it
    const query = queryString.parse(this.props.location.search);
    query.type = type;
    const queryAsString = queryString.stringify(query);

    if (replace) {
      this.props.history.replace({
        pathname: this.props.location.pathname,
        search: `?${queryAsString}`
      });
    } else {
      this.props.history.push({
        pathname: this.props.location.pathname,
        search: `?${queryAsString}`
      });
    }
  };

  onCategoryDropdownChange = (type: QueryReportType) => {
    this.setReportType(type);
  };

  showErrorSnackbar = (errorCode: number, errorDescription: string, reason: string) => {
    this.props.showSnack("SUBMIT_FEEDBACK_CONTAINER", {
      label: `${reason}: ${errorDescription} (Error Code ${errorCode})`,
      timeout: 5000
    });
  };

  getReportSection = () => {
    switch (this.state.activeReportType) {
      case QueryReportType.Feature:
        return (
          <FeatureRequestForm
            code={this.state.code as string}
            selectedApp={this.state.selectedApp}
            selectedTopic={this.state.selectedTopic}
            selectedParent={this.state.selectedParent}
          />
        );
      case QueryReportType.Bug:
        return (
          <BugReportForm
            code={this.state.code as string}
            selectedApp={this.state.selectedApp}
            selectedTopic={this.state.selectedTopic}
            selectedParent={this.state.selectedParent}
          />
        );
      case QueryReportType.ResourceRequest:
        return (
          <ResourceRequestForm
            code={this.state.code as string}
            selectedApp={this.state.selectedApp}
            selectedTopic={this.state.selectedTopic}
            selectedParent={this.state.selectedParent}
          />
        );
      default:
        return <p>Invalid Report Type!</p>;
    }
  };

  onParentSelected = (selected: FeedbackParent) => {
    this.setState(prevState => ({
      selectedParent: selected,
      userHasSelected: true,
      activeReportType:
        selected === FeedbackParent.Topic ? QueryReportType.Feature : prevState.activeReportType
    }));
  };

  onTopicDropdownSelected = (selected: Topic) => {
    this.setState({
      selectedTopic: selected,
      userHasSelected: true
    });
  };

  getParentDropdown = () => {
    switch (this.state.selectedParent) {
      case FeedbackParent.Application:
        return (
          <FeedbackAppDropdown
            value={this.state.selectedApp}
            onSelected={this.onAppDropdownSelected}
          />
        );
      case FeedbackParent.Topic:
        return (
          <FeedbackTopicDropdown
            value={this.state.selectedTopic}
            onSelected={this.onTopicDropdownSelected}
          />
        );
      default:
        throw new Error(`"${this.state.selectedParent}" is not a valid option`);
    }
  };

  getParentDropdownOptions = () => {
    const options = [FeedbackParent.Application];

    if (this.props.topics.length > 0) {
      options.push(FeedbackParent.Topic);
    }

    return options;
  };

  getCategoryOptions = () => {
    const options = [
      { category: FeedbackCategory.FeatureRequest, queryType: QueryReportType.Feature }
    ];

    if (this.state.selectedParent !== FeedbackParent.Topic) {
      options.push({
        category: FeedbackCategory.BugReport,
        queryType: QueryReportType.Bug
      });
      // TODO only some apps
      if (this.state.selectedApp?.allowResourceRequest) {
        options.push({
          category: FeedbackCategory.ResourceRequest,
          queryType: QueryReportType.ResourceRequest
        });
      }
    }

    return options;
  };

  render() {
    if (this.state.hasError && !!this.state.errorCode) {
      return (
        <ErrorDisplay
          errorCode={this.state.errorCode}
          errorDescription={this.state.errorDescription}
          errorResult={this.state.errorReason}
        />
      );
    }

    if (!this.state.codeIsGenerated && !this.props.appsLoadingState.isLoaded) {
      return <LoadingDisplay />;
    }

    return (
      <Container fluid>
        <h2>Create Feedback</h2>
        <br />
        <div className={styles.feedbackDropdownContainer}>
          <FeedbackParentDropdown
            value={this.state.selectedParent}
            onSelect={this.onParentSelected}
            options={this.getParentDropdownOptions()}
          />
          {this.getParentDropdown()}
          <FeedbackCategoryDropdown
            value={this.state.activeReportType}
            onSelect={this.onCategoryDropdownChange}
            options={this.getCategoryOptions()}
          />
        </div>
        <br />
        {this.getReportSection()}
      </Container>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(SubmitFeedbackContainer);
